import React, { useEffect, useCallback } from 'react';

import { AudioListenButton } from '@atlassian/ai-audio';

import { useActiveContent } from '@confluence/audio/entry-points/useActiveContent.ts';
import { useAudioContentQuery } from '@confluence/audio/entry-points/useAudioContentQuery.tsx';
import { useAudioButtonWrapperActions } from '@confluence/audio/entry-points/useAudioButtonWrapperStore.tsx';
import { expValEquals } from '@confluence/feature-experiments';
import { useAudioV3Changeboarding } from '@confluence/audio/entry-points/useAudioV3Changeboarding.ts';
import { AudioV3ChangeboardingSpotlight } from '@confluence/audio/entry-points/AudioV3ChangeboardingSpotlight.ts';
import { useReadTime } from '@confluence/read-time/entry-points/useReadTime.ts';

type AudioListenButtonWrapperProps = {
	isRovoButtonEnabled: boolean;
	contentId: string;
	contentType: string;
	spaceKey: string;
	title: string;
	isAutoOpenDisabled?: boolean;
};

export const AudioListenButtonWrapper = ({
	isRovoButtonEnabled,
	isAutoOpenDisabled,
	contentId,
	...props
}: AudioListenButtonWrapperProps) => {
	const { data } = useAudioContentQuery({ contentId });
	const activeContent = useActiveContent({ contentId, data, ...props });
	const noopOnShow = useCallback(() => {}, []);
	const { setActiveContent } = useAudioButtonWrapperActions();
	const { readTime } = useReadTime(contentId);

	useEffect(() => {
		if (data?.singleContent) {
			setActiveContent(activeContent);
		} else {
			setActiveContent(null);
		}
	}, [data?.singleContent, activeContent, setActiveContent]);

	const { showChangeboarding, handleCloseChangeboarding } = useAudioV3Changeboarding({
		contentId,
		onShow: noopOnShow,
		skip: !expValEquals('confluence_audio_v3_changeboarding', 'isEnabled', true),
		readTime,
	});

	const onShowMeClick = useCallback(() => {
		handleCloseChangeboarding();
	}, [handleCloseChangeboarding]);

	if (showChangeboarding && expValEquals('confluence_audio_v3_changeboarding', 'isEnabled', true)) {
		return (
			<>
				<AudioV3ChangeboardingSpotlight
					onDismissClick={handleCloseChangeboarding}
					onShowMeClick={onShowMeClick}
					activeContent={activeContent}
				>
					<AudioListenButton
						shape={isRovoButtonEnabled ? 'default' : 'circle'}
						activeContent={activeContent}
						isAutoOpenDisabled={!data?.singleContent || !!isAutoOpenDisabled}
						source="contentWrapper"
					/>
				</AudioV3ChangeboardingSpotlight>
			</>
		);
	}

	return (
		<AudioListenButton
			shape={isRovoButtonEnabled ? 'default' : 'circle'}
			activeContent={activeContent}
			isAutoOpenDisabled={!data?.singleContent || !!isAutoOpenDisabled}
			source="contentWrapper"
		/>
	);
};
